import type { ApiClient } from '@mbfab/client-api'
import { api } from '@mbfab/client-api'
import { isSome } from '@mbfab/shared'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { AppSidebarContainer } from '@/components/app/app-sidebar-container'
import { HistoryWorkspace } from '@/components/screens/upload/history-workspace'
import type { UploadFileType } from '@/components/screens/upload/upload-file-type'
import { uploadFileTypeOptions } from '@/components/screens/upload/upload-file-type'
import { runMutation as runMutationWithFeedback } from '@/lib/run-mutation'

// クエリ文字列など不定の値を UploadFileType に安全に変換する（v1 upload_history.tsx と同じ方針）
const parseUploadFileType = (value: unknown): UploadFileType | undefined =>
  uploadFileTypeOptions.find((x) => x.resourceType === value)?.resourceType

export const Route = createFileRoute('/t/$tenant_id/upload_history')({
  // ?type=... はアップロード画面からの種別引き継ぎに使う。不定の値は undefined に落とす
  validateSearch: (search: Record<string, unknown>): { type?: UploadFileType } => ({
    type: parseUploadFileType(search.type),
  }),
  component: UploadHistoryPage,
})

function UploadHistoryPage() {
  const { tenant_id: tenantId } = Route.useParams()
  return (
    <div className="flex min-h-screen">
      <AppSidebarContainer tenantId={tenantId} />
      <main className="min-w-0 flex-1">
        <UploadHistoryContainer />
      </main>
    </div>
  )
}

async function openS3ObjectFile(client: ApiClient, s3ObjectId: string) {
  try {
    const result = await client.fetchPagedS3Objects({ ids: [s3ObjectId] })
    const found = result.pagedS3Objects.s3Objects[0]
    if (isSome(found)) {
      window.open(found.fileUrl, '_blank', 'noopener,noreferrer')
    }
  } catch {
    // ダウンロード失敗は他の操作をブロックしない（無言で失敗させる）
  }
}

function UploadHistoryContainer() {
  const navigate = useNavigate()
  const { tenant_id: tenantId } = Route.useParams()
  const { type } = Route.useSearch()
  const client = api.useClient()
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [millsheetPage, setMillsheetPage] = useState(1)
  const [showUnlinkedMillsheets, setShowUnlinkedMillsheets] = useState(true)
  const [shearingPage, setShearingPage] = useState(1)
  const [nestingPage, setNestingPage] = useState(1)
  const pageSize = 100

  const millsheetFilesQuery = api.useFetchPagedS3ObjectsQuery({
    variables: {
      resourceType: 'millsheets',
      notConnectedOnly: showUnlinkedMillsheets,
      page: millsheetPage,
      pageSize,
    },
  })
  const millsheetsQuery = api.useFetchMillsheetsQuery({ variables: {} })
  const shearingQuery = api.useFetchPagedShearingCertificatePdfsQuery({
    variables: { resourceType: 'shearingCertificates', page: shearingPage, pageSize },
  })
  const nestingQuery = api.useFetchPagedNestingDiagramsQuery({ variables: { page: nestingPage, pageSize } })

  // 紐付け・紐付け解除・削除の mutation 実行 + トースト + ResultBanner 用 state 更新を共通ヘルパーに委譲する
  // （.claude/rules/ui-components.md「mutation を伴うボタンは、必ず結果のフィードバックを返す」）
  const runMutation = async (
    mutate: () => Promise<unknown>,
    successMessage: string,
    failureMessage: string,
  ): Promise<void> => {
    await runMutationWithFeedback({
      mutate,
      success: { message: successMessage },
      failure: { message: failureMessage },
      refetch: async () => {
        await Promise.all([
          millsheetFilesQuery.refetch(),
          millsheetsQuery.refetch(),
          shearingQuery.refetch(),
          nestingQuery.refetch(),
        ])
      },
      onResult: setFeedback,
    })
  }

  return (
    <HistoryWorkspace
      initialType={type}
      onNavigateToUpload={() => {
        void navigate({ to: '/t/$tenant_id/upload', params: { tenant_id: tenantId } })
      }}
      feedback={feedback}
      onDismissFeedback={() => {
        setFeedback(null)
      }}
      millsheet={{
        files: millsheetFilesQuery.data?.pagedS3Objects.s3Objects ?? [],
        unlinkedIds: new Set(
          showUnlinkedMillsheets ? (millsheetFilesQuery.data?.pagedS3Objects.s3Objects ?? []).map((x) => x.id) : [],
        ),
        millsheets: millsheetsQuery.data?.millsheets ?? [],
        autoLinkResults: {},
        loading: millsheetFilesQuery.loading,
        pagination: {
          page: millsheetPage,
          pageSize,
          totalCount: millsheetFilesQuery.data?.pagedS3Objects.totalCount ?? 0,
        },
        onChangePage: setMillsheetPage,
        onChangeLinkFilter: (unlinkedOnly) => {
          setShowUnlinkedMillsheets(unlinkedOnly)
          setMillsheetPage(1)
        },
        onLink: (s3ObjectId, millsheetId) => {
          void runMutation(
            async () => await client.updateS3Object({ input: { id: s3ObjectId, millsheetId } }),
            '紐付けました',
            '紐付けに失敗しました',
          )
        },
        onUnlink: (s3ObjectId) => {
          void runMutation(
            async () => await client.updateS3Object({ input: { id: s3ObjectId, millsheetId: null } }),
            '紐付けを解除しました',
            '解除に失敗しました',
          )
        },
        onDelete: (s3ObjectId) => {
          void runMutation(
            async () => await client.deleteS3Object({ id: s3ObjectId }),
            '削除しました',
            '削除に失敗しました',
          )
        },
        onDownload: (s3ObjectId) => {
          void openS3ObjectFile(client, s3ObjectId)
        },
      }}
      shearing={{
        rows: shearingQuery.data?.pagedS3Objects.s3Objects ?? [],
        loading: shearingQuery.loading,
        pagination: { page: shearingPage, pageSize, totalCount: shearingQuery.data?.pagedS3Objects.totalCount ?? 0 },
        onChangePage: setShearingPage,
        onDetach: (linkedShearingCertificateId) => {
          void runMutation(
            async () => await client.detachShearingCertificatePdf({ id: linkedShearingCertificateId }),
            'PDFの紐付けを解除しました',
            '解除に失敗しました',
          )
        },
        onDelete: (s3ObjectId) => {
          void runMutation(
            async () => await client.deleteS3Object({ id: s3ObjectId }),
            '削除しました',
            '削除に失敗しました',
          )
        },
        onDownload: (s3ObjectId) => {
          void openS3ObjectFile(client, s3ObjectId)
        },
      }}
      nesting={{
        rows: nestingQuery.data?.pagedNestingDiagrams.nestingDiagrams ?? [],
        loading: nestingQuery.loading,
        pagination: {
          page: nestingPage,
          pageSize,
          totalCount: nestingQuery.data?.pagedNestingDiagrams.totalCount ?? 0,
        },
        onChangePage: setNestingPage,
        onUpdateIndividualNumber: (id, individualNumber) => {
          void runMutation(
            async () => await client.updateNestingDiagram({ id, individualNumber }),
            '更新しました',
            '更新に失敗しました',
          )
        },
        onDelete: (id) => {
          void runMutation(async () => await client.deleteNestingDiagram({ id }), '削除しました', '削除に失敗しました')
        },
        onDownload: (s3ObjectId) => {
          void openS3ObjectFile(client, s3ObjectId)
        },
      }}
    />
  )
}
